import {
  Button,
  Flex,
  Heading,
  IconRefreshLine,
  Spinner,
  Text,
} from "@instructure/ui";
import { memo, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssessmentPlanService } from "../../../../services/AssessmentService";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import AssessmentCard from "../ComponentCards/AssessmentCard";
import EmbeddedDashboardCard from "../ComponentCards/EmbeddedDashboardCard";
import QualitativeMeasureCard from "../ComponentCards/QualitativeMeasureCard";
import AssessmentTemplateCard from "../ComponentCards/Template/AssessmentTemplateCard";
import EmbeddedDashTemplateCard from "../ComponentCards/Template/EmbeddedDashTemplateCard";
import QualMeasureTemplateCard from "../ComponentCards/Template/QualMeasureTemplateCard";
import SectionReviewersCard from "../Reviewers/SectionReviewersCard";
import { CANVAS, EMBEDDED, EXTERNAL, QUALITATIVE } from "../utils";

const TemplateSectionCard = memo(({ section }) => {
  const { sectionIndex, selectedCycle, handleCreateMeasure, extraData } =
    useContext(ApDataContext);
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [enableUpdate, setEnableUpdate] = useState(false);
  const [updatingSection, setUpdatingSection] = useState(false);

  const isReviewersEqual = (reviewers1, reviewers2) => {
    if (reviewers1.length !== reviewers2.length) {
      return false;
    }
    for (let i = 0; i < reviewers1.length; i++) {
      if (reviewers1[i] !== reviewers2[i]) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (section.id) {
      const currentSection = extraData.plan.sections.find(
        (s) => s.id === section.id
      );
      if (
        section.approvers !== currentSection.approvers ||
        !isReviewersEqual(section.reviewers, currentSection.reviewers)
      ) {
        setEnableUpdate(true);
      } else {
        setEnableUpdate(false);
      }
    }
  }, [extraData, section]);

  const handleUpdateSection = () => {
    setUpdatingSection(true);
    AssessmentPlanService.updateAssessmentPlanSection(
      section.id,
      section.name,
      section.description,
      section.approvers,
      section.reviewers.toString()
    )
      .then((updatedSection) => {
        extraData.plan.sections = extraData.plan.sections.map((s) =>
          s.id === section.id
            ? {
                ...s,
                approvers: updatedSection.approvers,
                reviewers: updatedSection.reviewers.split(","),
              }
            : s
        );
        setEnableUpdate(false);
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Section" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.updateError", { obj: "Section" }),
          ERROR
        )
      )
      .finally(() => setUpdatingSection(false));
  };

  return (
    <Card
      style={{
        backgroundColor: "#F1F1F1",
        border: "1px solid #ccc",
        marginTop: "0.75rem",
      }}
    >
      <Flex wrap="wrap" gap="small">
        <Flex.Item size="100%">
          <Flex gap="small" wrap="wrap">
            <Flex.Item shouldGrow>
              <Heading level="h3">{section.name}</Heading>
            </Flex.Item>
            {!!extraData.plan && (
              <Flex.Item>
                <Button
                  color="primary"
                  screenReaderLabel={t("assessment.sections.updateSection")}
                  size="small"
                  renderIcon={
                    updatingSection ? (
                      <Spinner
                        size="x-small"
                        renderTitle={t("global.pleaseWait")}
                      />
                    ) : (
                      IconRefreshLine
                    )
                  }
                  onClick={handleUpdateSection}
                  interaction={
                    enableUpdate && !updatingSection ? "enabled" : "disabled"
                  }
                >
                  {t("assessment.sections.updateSection")}
                </Button>
              </Flex.Item>
            )}
          </Flex>
        </Flex.Item>
        <Flex.Item size="100%">
          <RichTextViewer
            ariaLabel={t("assessment.sections.sectionDesc")}
            value={section.description}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <SectionReviewersCard section={section} />
        </Flex.Item>
        <Flex.Item size="100%">
          <Text weight="bold">{t("measures.measures")}:</Text>
          <Flex wrap="wrap" gap="small" margin="small 0 0 0">
            {section.measures.map((measure, key) => (
              <Flex.Item
                key={`section${sectionIndex}-measure${key}`}
                size="100%"
              >
                <ApDataContext.Provider
                  value={{
                    sectionIndex,
                    measureIndex: key,
                    selectedCycle,
                    handleCreateMeasure,
                    extraData,
                  }}
                >
                  {measure.id || measure.ddl_id ? (
                    <>
                      {(measure.type === CANVAS ||
                        measure.type === EXTERNAL) && (
                        <AssessmentCard assessment={measure} inTemplate />
                      )}
                      {measure.type === QUALITATIVE && (
                        <QualitativeMeasureCard measure={measure} inTemplate />
                      )}
                      {measure.type === EMBEDDED && (
                        <EmbeddedDashboardCard measure={measure} inTemplate />
                      )}
                    </>
                  ) : (
                    <>
                      {(measure.type === CANVAS ||
                        measure.type === EXTERNAL) && (
                        <AssessmentTemplateCard templateFields={measure} />
                      )}
                      {measure.type === QUALITATIVE && (
                        <QualMeasureTemplateCard templateFields={measure} />
                      )}
                      {measure.type === EMBEDDED && (
                        <EmbeddedDashTemplateCard templateFields={measure} />
                      )}
                    </>
                  )}
                </ApDataContext.Provider>
              </Flex.Item>
            ))}
          </Flex>
        </Flex.Item>
      </Flex>
    </Card>
  );
});

export default TemplateSectionCard;
